import { getApiUrl } from "@/utils/api";
import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Globe, MapPin, Loader2 } from "lucide-react";
import axios from "axios";

const API = getApiUrl();

const COUNTRY_FLAGS = {
  GB: "🇬🇧", US: "🇺🇸", AU: "🇦🇺", CA: "🇨🇦", IE: "🇮🇪",
  FR: "🇫🇷", DE: "🇩🇪", ES: "🇪🇸", IT: "🇮🇹", NL: "🇳🇱",
  IN: "🇮🇳", AE: "🇦🇪", SG: "🇸🇬", ZA: "🇿🇦", NZ: "🇳🇿",
};

const SOURCE_LABELS = {
  organic: "Organic Search",
  direct: "Direct",
  social: "Social Media",
  paid: "Paid / CPC",
  email: "Email",
  referral: "Referral",
};

const SOURCE_COLORS = {
  organic: "#10b981",
  direct: "#2f4f4f",
  social: "#3b82f6",
  paid: "#f59e0b",
  email: "#8b5cf6",
  referral: "#e07b5d",
};

export default function AnalyticsGeo({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState("countries");

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/analytics/geo?period=${period}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  const countries = data?.countries || [];
  const cities = data?.cities || [];
  const sources = data?.sources || [];
  const countryMax = Math.max(...countries.map((c) => c.count), 1);
  const cityMax = Math.max(...cities.map((c) => c.count), 1);

  const sourceChartData = sources.map((s) => ({
    name: SOURCE_LABELS[s.source] || s.source,
    count: s.count,
    color: SOURCE_COLORS[s.source] || "#6b7280",
  }));

  return (
    <div className="space-y-5" data-testid="analytics-geo">
      {/* Sub tabs */}
      <div className="flex flex-wrap gap-1 border-b border-border">
        {[
          { id: "countries", label: "Countries", icon: Globe },
          { id: "cities", label: "Cities", icon: MapPin },
          { id: "sources", label: "Traffic Sources", icon: Globe },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              subTab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "countries" && (
        <div className="bg-white border border-border rounded-lg p-5">
          <h3 className="font-serif text-sm font-medium mb-4">Sessions by Country</h3>
          {countries.length > 0 ? (
            <div className="space-y-2">
              {countries.map((c, i) => (
                <div key={i} className="py-1.5">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-base leading-none">{COUNTRY_FLAGS[c.country] || "🌐"}</span>
                      <span className="font-medium">{c.country}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold">{c.count.toLocaleString()}</span>
                      <span className="text-muted-foreground w-8 text-right">
                        {Math.round(c.count / countryMax * 100)}%
                      </span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${(c.count / countryMax) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No geographic data yet — detected from browser locale
            </p>
          )}
        </div>
      )}

      {subTab === "cities" && (
        <div className="bg-white border border-border rounded-lg p-5">
          <h3 className="font-serif text-sm font-medium mb-1">Sessions by City</h3>
          <p className="text-xs text-muted-foreground mb-4">Derived from browser timezone — approximate location</p>
          {cities.length > 0 ? (
            <div className="space-y-2">
              {cities.map((c, i) => (
                <div key={i} className="py-1.5">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      <span className="font-medium">{c.city}</span>
                    </div>
                    <span className="font-bold">{c.count.toLocaleString()}</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(c.count / cityMax) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No city data yet</p>
          )}
        </div>
      )}

      {subTab === "sources" && (
        <div className="space-y-4">
          {sourceChartData.length > 0 ? (
            <>
              <div className="bg-white border border-border rounded-lg p-5">
                <h3 className="font-serif text-sm font-medium mb-4">Traffic Sources</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={sourceChartData} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip
                      content={({ active, payload, label }) =>
                        active && payload?.length ? (
                          <div className="bg-white border border-border shadow-md p-2 rounded text-xs">
                            <p className="font-medium">{label}</p>
                            <p>{payload[0]?.value.toLocaleString()} sessions</p>
                          </div>
                        ) : null
                      }
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {sourceChartData.map((s, i) => (
                        <Cell key={i} fill={s.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {sourceChartData.map((s, i) => (
                  <div key={i} className="bg-white border border-border rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-xs text-muted-foreground">{s.name}</span>
                    </div>
                    <p className="text-xl font-bold font-serif">{s.count.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="bg-white border border-border rounded-lg p-8 text-center">
              <p className="text-sm text-muted-foreground">No traffic source data yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
